import { RadioGroup } from "@headlessui/react";
import {
  MenuAddonGroup,
  MenuAddonItem,
  MenuItem,
  MenuOption,
} from "../hooks/useMenu";
import { CartItem, useCartStore } from "../store/CartStore";
import QuantityInput from "./QuantityInput";
import { CheckIcon, MinusIcon, PlusIcon } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { cn, formatPriceToDkk, getImageUrl } from "@/lib/utils";
import { useTrackingEvent } from "@/hooks/useTrackingEvent";
import { motion, AnimatePresence } from "framer-motion";
import { locationRoute } from "@/App";
import { useFlags } from "@/hooks/useFlags";
import { LocationConfigurationContext } from "@/providers/LocationConfigurationProvider";

// --- Define a type for a single Condiment ---
export interface Condiment {
  id: number;
  name: string;
  price: number;
}

// --- Define a type for a condiment group on a product option ---
type MenuCondimentGroup = {
  groupId: number;
  name: string;
  // rule can be null or an object with min/max counts
  rule: { minimumCount: number; maximumCount: number } | null;
  condiments: Condiment[];
};

type ProductPaneProps = {
  isOpen?: boolean;
  onClose: () => unknown;
  product: MenuItem & { isUpsale?: boolean };
  itemInCart?: CartItem;
};

const ProductPane = ({
  /*isOpen,*/ onClose,
  product,
  itemInCart,
}: ProductPaneProps) => {
  const { add, setCount } = useCartStore();
  const { locationId } = locationRoute.useParams();
  const { addonsAvailable } = useFlags();
  const { rebate } = useContext(LocationConfigurationContext);
  const tracking = useTrackingEvent();

  // Quantity, variant, addons state…
  const [quantity, setQuantity] = useState(1);
  const [variant, setVariant] = useState<MenuOption | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<
    (MenuAddonItem & { count: number })[]
  >([]);
  // NEW: State for condiments (each condiment augmented with a count)
  const [selectedCondiments, setSelectedCondiments] = useState<
    (Condiment & { count: number })[]
  >([]);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [hasEdited, setHasEdited] = useState<boolean>(false);

  function checkIfEdited() {
    return itemInCart
      ? JSON.stringify({
          id: variant?.id,
          addons: addonsAvailable ? selectedAddons ?? [] : [],
          cartCondiment: variant?.condiments ? selectedCondiments ?? [] : [],
          count: quantity,
        }) !==
          JSON.stringify({
            id: itemInCart?.id,
            addons: addonsAvailable ? itemInCart?.addons : [],
            cartCondiment: itemInCart?.cartCondiment ?? [],
            count: itemInCart.count,
          })
      : false;
  }

  // When editing an item in the cart, load its variant, addons, and condiments
  useEffect(() => {
    if (itemInCart) {
      setIsUpdating(true);
      const variantToSelect =
        product.options.find((option) => option.id === itemInCart.id) ??
        product.options.find((option) => option.default) ??
        product.options[0];

      setVariant(variantToSelect);
      setSelectedAddons(addonsAvailable ? itemInCart.addons ?? [] : []);
      // If the item already has condiments, load them.
      setSelectedCondiments(itemInCart.cartCondiment ?? []);
      setQuantity(itemInCart.count);
      setHasEdited(checkIfEdited());
    }
  }, [itemInCart]);

  // When first loading a product (no cart item) set defaults
  useEffect(() => {
    if (product && product.options && !itemInCart) {
      const defaultVariant =
        product.options.find((option) => option.default) ?? product.options[0];
      setVariant(defaultVariant);

      // Select all required addons on load
      const requiredAddons =
        addonsAvailable && product.addonGroups
          ? product.addonGroups
              .map((group) =>
                group.items
                  .filter((addon) => addon.min > 0)
                  .map((addon) => ({ ...addon, count: addon.min }))
              )
              .flat()
          : [];
      setSelectedAddons(requiredAddons);

      // If the default variant includes condiments, preselect required condiments.
      if (defaultVariant.condiments && defaultVariant.condiments.length > 0) {
        defaultVariant.condiments.forEach((group: MenuCondimentGroup) => {
          const min = group.rule?.minimumCount || 0;
          if (min > 0) {
            const defaults = group.condiments
              .slice(0, min)
              .map((c) => ({ ...c, count: 1 }));
            setSelectedCondiments((prev) => [...prev, ...defaults]);
          }
        });
      }
    }
  }, [product]);

  // Reset quantity (and condiments) when variant changes and not updating.
  useEffect(() => {
    if (variant && !isUpdating) {
      setQuantity(1);
      // Reset condiments and preselect required ones if available.
      setSelectedCondiments([]);
      if (variant.condiments && variant.condiments.length > 0) {
        variant.condiments.forEach((group: MenuCondimentGroup) => {
          const min = group.rule?.minimumCount || 0;
          if (min > 0) {
            const defaults = group.condiments
              .slice(0, min)
              .map((c) => ({ ...c, count: 1 }));
            setSelectedCondiments((prev) => [...prev, ...defaults]);
          }
        });
      }

      // if requiredProductOptionsIds is set on addon groups, filter selected addons to only those valid for the new variant
      const filteredAddons = selectedAddons.filter((addon) => {
        const group = product.addonGroups.find((group) =>
          group.items.some((item) => item.id === addon.id)
        );
        if (group?.requiredProductOptionIds) {
          return group.requiredProductOptionIds.includes(variant?.id ?? "");
        }
        return true;
      });
      setSelectedAddons(filteredAddons);
    }
  }, [variant]);

  useEffect(() => {
    setHasEdited(checkIfEdited());
  }, [variant, selectedAddons, selectedCondiments, quantity]);

  function resetToDefault() {
    setIsUpdating(false);
    setHasEdited(false);
    setVariant(
      product.options.find((option) => option.default) ?? product.options[0]
    );
    setSelectedAddons([]);
    setSelectedCondiments([]);
    setQuantity(1);
  }

  function handleAddToCart() {
    setShowAddonErrors(false);
    if (!variant) return;

    if (!addonsAreValid) {
      setShowAddonErrors(true);
      return;
    }

    // If updating, remove current item before re-adding.
    if (isUpdating && itemInCart) setCount(itemInCart.lineId, 0);

    // Add the item with both addons and condiments.
    add(
      {
        ...variant,
        addons: selectedAddons ?? [],
        cartCondiment: selectedCondiments ?? [],
        isUpsale: product?.isUpsale ?? false,
      },
      quantity
    );
    try {
      tracking.capture(
        product?.isUpsale ? "upsale_added_to_cart" : "product_added_to_cart",
        {
          locationId: locationId,
          product_name: product.name,
          variant_name: variant.name,
          quantity,
        }
      );
    } catch (error) {
      console.error("Error capturing add to cart event:", error);
    }
    onClose();
  }

  function removeFromCart() {
    if (!itemInCart) return;
    setCount(itemInCart.lineId, 0);
    onClose();
  }

  const [addonErrors, setAddonErrors] = useState<string[]>([]);
  const [showAddonErrors, setShowAddonErrors] = useState<boolean>(false);

  const hasAddons = addonsAvailable
    ? product?.addonGroups !== undefined && product?.addonGroups?.length > 0
    : false;
  const addonGroups =
    product.addonGroups.filter((group) => {
      // If the group has requiredProductOptionIds, check if the selected variant matches
      if (
        group.requiredProductOptionIds &&
        group.requiredProductOptionIds.length > 0
      ) {
        return group.requiredProductOptionIds.includes(variant?.id ?? "");
      }
      return true;
    }) ?? [];

  const hasMultipleOptions = product.options.length > 1;
  const hasOptions = addonGroups.length > 0 || hasMultipleOptions;
  const hasRequiredAddons = hasAddons
    ? addonGroups.find((group) => group.min > 0)
    : false;

  useEffect(() => {
    setShowAddonErrors(false);
    if (!hasRequiredAddons) return;
    if (addonsAvailable === false) return;

    const requiredAddonGroups = addonGroups.filter((group) => group.min > 0);
    const groupsWithErrors = [];

    for (const group of requiredAddonGroups) {
      const addonIdsInGroup = group.items?.map((addon) => addon.id);
      const selectedAddonsInGroup = selectedAddons.filter((addon) =>
        addonIdsInGroup.includes(addon.id)
      );
      const count = selectedAddonsInGroup.reduce(
        (total, addon) => total + addon.count,
        0
      );
      if (count < group.min) groupsWithErrors.push(group.id);
    }
    setAddonErrors(groupsWithErrors);
  }, [selectedAddons]);

  useEffect(() => {
    if (addonErrors.length === 0) return;
    if (!showAddonErrors) return;

    const firstGroupId = addonErrors[0] ?? "";
    const firstGroup = document.getElementById(firstGroupId);
    if (!firstGroup) return;
    firstGroup.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [showAddonErrors, addonErrors]);

  const addonsAreValid = addonsAvailable
    ? hasRequiredAddons
      ? addonErrors.length < 1
      : true
    : true;

  const callToAction = () => {
    if (isUpdating && hasEdited) return "Update";
    if (isUpdating) return "Remove";
    if (showAddonErrors) return "Check selection";
    return "Add to order";
  };

  // Determine if the current variant has condiments.
  const hasCondiments =
    variant && variant.condiments && variant.condiments.length > 0;

  const image = useMemo(() => {
    if (variant && variant.image) return variant.image;
    if (product && product.image) return product.image;
  }, [product, variant]);

  const hasImage = image && image !== "/images/irondice.jpeg";

  if (!product) return null;

  // Shared content component
  const ProductContent = () => (
    <div className="space-y-2 md:space-y-4 pb-24 lg:pb-0">
      {product.description && (
        <div className="px-4 pt-4">
          <p className="whitespace-pre-line text-sm">{product.description}</p>
        </div>
      )}

      <div className="space-y-2 divide-y">
        {hasMultipleOptions && (
          <div className="mx-auto w-full px-4">
            <RadioGroup
              value={variant}
              onChange={(value) => setVariant(value)}
              className="space-y-2"
            >
              <RadioGroup.Label className="font-semibold text-sm hidden">
                Select an option
              </RadioGroup.Label>
              {product.options.map((opt) => {
                const optSavings =
                  rebate && product.categoryId === rebate.categoryId
                    ? Math.floor(opt.quantity / rebate.setSize) * rebate.discountPerSet
                    : 0;
                return (
                <RadioGroup.Option
                  key={opt.id}
                  value={opt}
                  className="max-md:outline-none cursor-pointer select-none"
                >
                  {({ checked }) => (
                    <div
                      className={classNames(
                        "flex w-full items-center justify-between p-3 outline-none shadow-sm border rounded-md relative",
                        {
                          "mt-4": opt.popular,
                          "bg-primary text-primary-foreground": checked,
                          "bg-background text-foreground hover:bg-muted/40":
                            !checked,
                        }
                      )}
                    >
                      {opt.popular && (
                        <div className="text-primary-foreground text-[12px] absolute -top-2.5 -left-2 bg-primary px-2 py-0.5 shadow-md rounded-full">
                          Popular
                        </div>
                      )}
                      <div className="flex w-full items-center gap-x-4">
                        <div
                          className={cn(
                            "rounded-full border p-2 h-4 w-4 flex items-center justify-center",
                            checked
                              ? "bg-background text-primary"
                              : "bg-transparent text-black"
                          )}
                        >
                          {checked && (
                            <div className="shrink-0">
                              <CheckIcon
                                strokeWidth="2px"
                                className={classNames("h-3 w-3")}
                              />
                            </div>
                          )}
                        </div>
                        <div
                          className="text-sm flex gap-x-4 font-medium items-center w-full"
                          id={`headlessui-label-${opt.id}`}
                        >
                          <span>{opt.name}</span>
                          <div className="ml-auto flex items-center gap-x-4">
                            {optSavings > 0 ? (
                              <div className="flex flex-col items-end">
                                <span className={cn("line-through text-xs", checked ? "text-primary-foreground/60" : "text-muted-foreground")}>
                                  {formatPriceToDkk(opt.price)}
                                </span>
                                <span>{formatPriceToDkk(opt.price - optSavings)}</span>
                              </div>
                            ) : (
                              <span>{formatPriceToDkk(opt.price)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </RadioGroup.Option>
                );
              })}
            </RadioGroup>
          </div>
        )}

        {hasAddons && (
          <div className="w-full px-4 py-4 flex flex-col gap-y-2">
            {addonGroups.map((group) => {
              const error = showAddonErrors
                ? addonErrors.find((id) => id === group.id)
                  ? true
                  : false
                : false;
              return (
                <Addons
                  {...group}
                  setSelectedAddons={setSelectedAddons}
                  selectedAddons={selectedAddons}
                  key={group.id}
                  error={error}
                />
              );
            })}
          </div>
        )}

        {hasCondiments && (
          <div className="w-full px-4 py-4 flex flex-col gap-y-2">
            {(variant!.condiments as MenuCondimentGroup[]).map((group) => (
              <CondimentGroup
                key={group.groupId}
                groupId={group.groupId.toString()}
                name={group.name}
                min={group.rule?.minimumCount || 0}
                max={group.rule?.maximumCount || 0}
                items={group.condiments}
                selectedItems={selectedCondiments}
                setSelectedItems={setSelectedCondiments}
                error={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Shared header component
  const ProductHeader = () => (
    <header
      className={cn(
        "px-4 border-b flex flex-col gap-y-0.5 bg-background flex-shrink-0",
        hasImage ? "py-2" : "py-4",
        // Mobile specific styling
        "lg:rounded-none rounded-t-xl"
      )}
    >
      <h3 className="text-lg font-medium font-sans tracking-tighter">
        {product.name}
      </h3>
      {variant && (
        <div className="flex items-center gap-x-2 min-h-5">
          <span className="text-sm heading-typography">
            {variant.name} - {formatPriceToDkk(variant.price)}
          </span>
          {variant.popular && (
            <div className="text-primary-foreground text-xs py-0.5 bg-primary px-2 shadow-md rounded-full">
              Popular
            </div>
          )}
        </div>
      )}
    </header>
  );

  // Shared footer component
  const ProductFooter = ({ className }: { className?: string }) => (
    <footer
      className={cn(
        "flex flex-col gap-y-2 p-4 border-t z-20 bg-background shadow-lg backdrop-blur-sm",
        className
      )}
    >
      <div className="flex flex-row gap-x-4 heading-typography">
        <div className="p-2 rounded-md border bg-background">
          <QuantityInput value={quantity} onChange={setQuantity} />
        </div>
        <button
          type="button"
          onClick={() => {
            if (variant) {
              isUpdating && !hasEdited ? removeFromCart() : handleAddToCart();
            }
          }}
          className={cn(
            "relative w-full flex flex-wrap items-center px-4 justify-between rounded-md border py-2 text-sm font-medium cursor-pointer gap-2",
            isUpdating && !hasEdited
              ? "bg-red-500 text-white"
              : "bg-primary text-primary-foreground"
          )}
        >
          <span className="whitespace-pre-wrap text-sm">{callToAction()}</span>{" "}
          {(() => {
            const buttonPrice =
              (variant?.price ?? 0) * quantity +
              selectedAddons.reduce(
                (total, addon) => total + addon.price * addon.count * quantity,
                0
              ) +
              selectedCondiments.reduce(
                (total, condiment) =>
                  total + condiment.price * condiment.count * quantity,
                0
              );
            const buttonSavings =
              rebate && variant && product.categoryId === rebate.categoryId
                ? Math.floor(
                    (quantity * variant.quantity) / rebate.setSize
                  ) * rebate.discountPerSet
                : 0;
            return buttonSavings > 0 ? (
              <span className="flex flex-col items-end">
                <span className="line-through text-xs opacity-60">
                  {formatPriceToDkk(buttonPrice)}
                </span>
                <span>{formatPriceToDkk(buttonPrice - buttonSavings)}</span>
              </span>
            ) : (
              <span>{formatPriceToDkk(buttonPrice)}</span>
            );
          })()}
        </button>
      </div>

      {isUpdating && hasOptions && (
        <div className="flex gap-2 px-4 text-center text-sm justify-center w-full">
          <button
            type="button"
            onClick={() => resetToDefault()}
            className="underline"
          >
            Add another with different options
          </button>
        </div>
      )}
    </footer>
  );

  return (
    <>
      {/* Mobile/Tablet Layout */}
      <div className="lg:hidden bg-background text-foreground w-full h-full flex flex-col min-h-[40vh] max-h-[90vh] md:max-h-[80vh] rounded-t-xl overflow-hidden relative">
        <div className="w-full h-full overflow-y-auto">
          {/* Fixed Image */}
          {hasImage && (
            <img
              src={getImageUrl(locationId, image)}
              alt={product.name + (variant ? ` - ${variant?.name}` : "")}
              width="256"
              height="256"
              loading="lazy"
              decoding="async"
              className="sticky top-0 h-auto w-full max-h-[40vh] object-cover bg-gray-50 object-center select-none z-0 rounded-t-xl"
            />
          )}

          {/* Content that scrolls over image */}
          <div className="bg-background rounded-t-xl -mt-2.5 relative z-[1]">
            <ProductHeader />
            <ProductContent />
          </div>
        </div>

        {/* Mobile footer */}
        <ProductFooter className="sticky bottom-0 w-full" />
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block bg-background text-foreground w-full h-full min-h-[40vh] max-h-[90vh] md:max-h-[80vh] rounded-xl overflow-hidden relative">
        <div
          className={cn(
            "h-full",
            hasImage ? "grid grid-cols-2" : "flex flex-col"
          )}
        >
          {/* Image Section - only show if has image */}
          {hasImage && (
            <img
              src={getImageUrl(locationId, image)}
              alt={product.name + (variant ? ` - ${variant?.name}` : "")}
              width="256"
              height="256"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover bg-gray-50 object-center select-none rounded-l-xl md:max-h-[80vh]"
            />
          )}

          {/* Content Section */}
          <div
            className={cn(
              "flex flex-col overflow-hidden relative",
              hasImage ? "" : "flex-1"
            )}
          >
            <div className="flex-1 overflow-y-auto pb-24">
              <ProductHeader />
              <ProductContent />
            </div>
            {/* Desktop footer */}
            <ProductFooter className="absolute bottom-0 left-0 right-0" />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPane;

// ---------------------------------------------------------------------
// Addons & AddonItem components (unchanged from your existing implementation)
// ---------------------------------------------------------------------
function Addons(
  props: MenuAddonGroup & {
    selectedAddons: (MenuAddonItem & { count: number })[];
    setSelectedAddons: (value: (MenuAddonItem & { count: number })[]) => void;
    error?: boolean;
  }
) {
  const {
    id,
    name,
    min,
    max,
    items,
    selectedAddons,
    setSelectedAddons,
    error = "",
  } = props;

  const addonsIdsInGroup = items.map((addon) => addon.id);
  const selectedAddonCount = selectedAddons
    .filter((addon) => addonsIdsInGroup.includes(addon.id))
    .reduce((total, addon) => total + addon.count, 0);

  const canSelectAddons = max === 0 ? true : selectedAddonCount < max;
  const hasSelectedAddon = selectedAddonCount > 0;
  const isRequired = min > 0;
  const isBoolean = min === 1 && max === 1;

  useEffect(() => {
    if (!hasSelectedAddon && isRequired && isBoolean) {
      selectAddon(addonsIdsInGroup[0]);
    }
  }, [isRequired, isBoolean, addonsIdsInGroup]);

  function selectAddon(addonId: string) {
    const alreadySelectedAddon = selectedAddons.find(
      (selectedAddon) => selectedAddon.id === addonId
    );
    const addon = items.find((addon) => addon.id === addonId);
    if (!addon) return;

    // If boolean, ie: you can only select one of X addons, we don't disable the button
    if (isBoolean && !alreadySelectedAddon) {
      const selectedAddonInGroup = selectedAddons.find((selectedAddon) =>
        items.some((item) => item.id === selectedAddon.id)
      );
      setSelectedAddons([
        ...selectedAddons.filter(
          (selectedAddon) => selectedAddon.id !== selectedAddonInGroup?.id
        ),
        { ...addon, count: 1 },
      ]);
      return;
    }

    if (!canSelectAddons) return;

    if (alreadySelectedAddon) {
      setSelectedAddons([
        ...selectedAddons.filter(
          (selectedAddon) => selectedAddon.id !== addonId
        ),
        { ...alreadySelectedAddon, count: alreadySelectedAddon.count + 1 },
      ]);
    } else {
      setSelectedAddons([...selectedAddons, { ...addon, count: 1 }]);
    }
  }

  function unselectAddon(addonId: string) {
    if (isBoolean && isRequired) return;
    const firstInstance = selectedAddons.find((addon) => addon.id === addonId);
    if (!firstInstance) return;
    if (firstInstance.count > 1) {
      const updatedInstance = {
        ...firstInstance,
        count: firstInstance.count - 1,
      };
      setSelectedAddons([
        ...selectedAddons.filter(
          (selectedAddon) => selectedAddon.id !== addonId
        ),
        updatedInstance,
      ]);
    } else {
      setSelectedAddons(selectedAddons.filter((addon) => addon.id !== addonId));
    }
  }

  const subtitle = () => {
    if (isRequired && min - selectedAddonCount > 0)
      return `Please select at least ${
        min - selectedAddonCount === 1
          ? "1"
          : `${min - selectedAddonCount} more`
      }`;
    if (max !== 0 && canSelectAddons && hasSelectedAddon)
      return `Add up to ${max - selectedAddonCount} more`;
    if (max !== 0 && canSelectAddons) return `You can add up to ${max}`;
    if (max !== 0 && !canSelectAddons && !isBoolean)
      return "You've selected the maximum";
    return "";
  };

  return (
    <article id={id}>
      <div className="flex flex-col gap-y-0">
        <p className="font-medium tracking-tight text-sm heading-typography">
          {name}
        </p>
        <span
          className={cn(
            "text-xs transition",
            error ? "text-red-500" : "text-muted-foreground"
          )}
        >
          {subtitle()}
        </span>
      </div>
      <ul className="flex flex-col gap-y-2 py-2">
        {items.map((addon) => {
          const addonInCart = selectedAddons.find(
            (selectAddon) => selectAddon.id === addon.id
          );
          const countInCart = addonInCart?.count ?? 0;
          const isSelected = addonInCart !== undefined;
          return (
            <AddonItem
              {...addon}
              key={addon.id}
              selectAddon={selectAddon}
              unselectAddon={unselectAddon}
              isSelected={isSelected}
              canSelectAddons={canSelectAddons}
              countInCart={countInCart}
            />
          );
        })}
      </ul>
    </article>
  );
}

function AddonItem(
  props: MenuAddonItem & {
    selectAddon: (value: string) => void;
    unselectAddon: (value: string) => void;
    isSelected: boolean;
    canSelectAddons: boolean;
    countInCart: number;
  }
) {
  const {
    id,
    name,
    price,
    isSelected,
    countInCart,
    canSelectAddons,
    min,
    max,
  } = props;
  const [tapped, setTapped] = useState<boolean>(false);
  const isRequired = min > 0;
  const hasMaxiumum = max !== 0;
  const hasReachedMaxium = hasMaxiumum ? countInCart === max : false;
  const canSelect = canSelectAddons && !hasReachedMaxium;
  const canSelectMultiple = max !== 1;
  const canUnselect = isRequired ? countInCart - 1 > min : true;

  function getSubtitle() {
    if (!canSelectAddons) return "Can't add any more";
    if (isRequired && countInCart < min)
      return `Please add at least ${min - countInCart}`;
    if (!hasMaxiumum) return "";
    if (countInCart > 0 && !hasReachedMaxium)
      return `Add up to ${max - countInCart} more`;
    if (hasReachedMaxium && max > 1) return `You cannot add any more`;
    if (countInCart === 0 && max > 1) return `Add up to ${max}`;
  }

  const subtitle = getSubtitle();

  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-md border border-primary/30 relative transition opacity-100"
      )}
    >
      <button
        type="button"
        className={cn("flex justify-between py-3 px-2 text-sm w-full")}
        onClick={() => {
          setTapped(false);
          if (canSelect) {
            props.selectAddon(id);
            setTapped(true);
            return;
          }
          if (!canSelectMultiple) {
            isSelected
              ? canUnselect && props.unselectAddon(id)
              : props.selectAddon(id);
          }
          setTapped(true);
        }}
      >
        <div className="flex gap-2 items-center max-w-[85%]">
          <div
            className={cn(
              "rounded-full border border-primary/30 p-2 h-5 w-5 flex items-center justify-center",
              isSelected
                ? "bg-primary text-primary-foreground"
                : "bg-transparent text-foreground",
              tapped && "animate-button-tap"
            )}
            onAnimationEnd={() => setTapped(false)}
          >
            {isSelected && (
              <div className="shrink-0 text-[10px]">
                {max === 1 ? (
                  <CheckIcon
                    strokeWidth="2px"
                    className={classNames("h-3 w-3")}
                  />
                ) : (
                  countInCart.toString()
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col justify-start items-start gap-y-0">
            <p className="font-medium tracking-tight whitespace-nowrap text-ellipsis overflow-hidden heading-typography">
              {name}
            </p>
            <span className="text-[10px] leading-[10px] text-muted-foreground">
              {subtitle}
            </span>
          </div>
        </div>
      </button>
      <div
        className={cn(
          "absolute right-0 top-1/2 -translate-y-1/2 flex gap-x-2 items-center bg-background before:z-[-1] before:bg-gradient-to-r before:from-transparent before:via-background before:to-background z-[1] before:absolute before:top-0 before:bottom-0 before:right-[25%] before:w-[130%] before:h-full px-2 before:pointer-events-none",
          isSelected && canSelectMultiple ? "" : "pointer-events-none"
        )}
      >
        <AnimatePresence>
          <motion.span
            className={cn("text-xs")}
            layout
            transition={{ type: "ease-in-out" }}
            key="price"
          >
            {price === 0 ? (
              ""
            ) : (
              <span className="whitespace-nowrap">{`+ ${formatPriceToDkk(
                price
              )}`}</span>
            )}
          </motion.span>
          {canSelectMultiple && (
            <motion.div
              transition={{ type: "ease-in-out" }}
              key="buttons"
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn(
                "flex items-center justify-center h-full gap-x-2 pr-2"
              )}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setTapped(false);
                  props.unselectAddon(id);
                  setTapped(true);
                }}
                className="transition bg-background text-foreground flex items-center justify-center rounded-full border py-1 px-2 active:bg-primary active:text-primary-foreground"
                disabled={!canUnselect}
              >
                <MinusIcon className="w-4" strokeWidth="2px" />
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setTapped(false);
                  props.selectAddon(id);
                  setTapped(true);
                }}
                className="transition bg-background text-foreground flex items-center justify-center disabled:opacity-75 rounded-full border py-1 px-2 active:bg-primary active:text-primary-foreground"
                disabled={!canSelect}
              >
                <PlusIcon className="w-4" strokeWidth="2px" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// CondimentGroup & CondimentItem components
// These mirror the Addons components but work with condiment data.
// ---------------------------------------------------------------------
type CondimentGroupProps = {
  groupId: string;
  name: string;
  min: number;
  max: number;
  items: Condiment[];
  selectedItems: (Condiment & { count: number })[];
  setSelectedItems: (value: (Condiment & { count: number })[]) => void;
  error?: boolean;
};

function CondimentGroup(props: CondimentGroupProps) {
  const {
    groupId,
    name,
    min,
    max,
    items,
    selectedItems,
    setSelectedItems,
    error,
  } = props;

  const groupItemIds = items.map((item) => item.id);
  const selectedCount = selectedItems
    .filter((item) => groupItemIds.includes(item.id))
    .reduce((total, item) => total + item.count, 0);

  const canSelectItems = max === 0 ? true : selectedCount < max;
  const isRequired = min > 0;
  const isBoolean = min === 1 && max === 1;

  // Preselect if required and nothing is selected.
  useEffect(() => {
    if (!selectedCount && isRequired && isBoolean) {
      selectItem(items[0].id);
    }
  }, [isRequired, isBoolean, items]);

  function selectItem(itemId: number) {
    const alreadySelected = selectedItems.find((item) => item.id === itemId);
    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    if (isBoolean && !alreadySelected) {
      const existing = selectedItems.find((selected) =>
        items.some((i) => i.id === selected.id)
      );
      setSelectedItems([
        ...selectedItems.filter((i) => i.id !== existing?.id),
        { ...item, count: 1 },
      ]);
      return;
    }

    if (!canSelectItems) return;

    if (alreadySelected) {
      setSelectedItems([
        ...selectedItems.filter((i) => i.id !== itemId),
        { ...alreadySelected, count: alreadySelected.count + 1 },
      ]);
    } else {
      setSelectedItems([...selectedItems, { ...item, count: 1 }]);
    }
  }

  function unselectItem(itemId: number) {
    if (isBoolean && isRequired) return;
    const instance = selectedItems.find((item) => item.id === itemId);
    if (!instance) return;
    if (instance.count > 1) {
      setSelectedItems([
        ...selectedItems.filter((i) => i.id !== itemId),
        { ...instance, count: instance.count - 1 },
      ]);
    } else {
      setSelectedItems(selectedItems.filter((i) => i.id !== itemId));
    }
  }

  const subtitle = () => {
    if (isRequired && min - selectedCount > 0)
      return `Please select at least ${min - selectedCount}`;
    if (max !== 0 && canSelectItems && selectedCount > 0)
      return `Add up to ${max - selectedCount} more`;
    if (max !== 0 && canSelectItems) return `You can add up to ${max}`;
    if (max !== 0 && !canSelectItems && !isBoolean)
      return "You've selected the maximum";
    return "";
  };

  return (
    <article id={groupId}>
      <div className="flex flex-col gap-y-0">
        <p className="font-medium tracking-tight text-sm heading-typography">
          {name}
        </p>
        <span
          className={classNames(
            "text-xs transition",
            error ? "text-red-500" : "text-muted-foreground"
          )}
        >
          {subtitle()}
        </span>
      </div>
      <ul className="flex flex-col gap-y-2 py-2">
        {items.map((item) => {
          const selectedItem = selectedItems.find((i) => i.id === item.id);
          const countInCart = selectedItem?.count || 0;
          const isSelected = !!selectedItem;
          return (
            <CondimentItem
              key={item.id}
              {...item}
              selectItem={selectItem}
              unselectItem={unselectItem}
              isSelected={isSelected}
              canSelect={canSelectItems}
              countInCart={countInCart}
              min={min}
              max={max}
            />
          );
        })}
      </ul>
    </article>
  );
}

type CondimentItemProps = Condiment & {
  selectItem: (id: number) => void;
  unselectItem: (id: number) => void;
  isSelected: boolean;
  canSelect: boolean;
  countInCart: number;
  min: number;
  max: number;
};

function CondimentItem(props: CondimentItemProps) {
  const {
    id,
    name,
    price,
    isSelected,
    countInCart,
    canSelect, // Using canSelect from props
    min,
    max,
    selectItem,
    unselectItem,
  } = props;
  const [tapped, setTapped] = useState(false);
  const isRequired = min > 0;
  const hasMaximum = max !== 0;
  const hasReachedMax = hasMaximum ? countInCart === max : false;
  const canSelectNow = canSelect && !hasReachedMax; // Combined canSelect (group limit) and !hasReachedMax (item limit)
  const canSelectMultiple = max !== 1;
  // Corrected canUnselect logic: ensure count doesn't go below required minimum
  const canUnselect = isRequired ? countInCart - 1 >= min : countInCart > 0;

  function getSubtitle() {
    // Assuming getSubtitle logic remains the same as in the original code block
    if (!canSelect && !isSelected) return ""; // Group max reached, can't add more
    if (isRequired && countInCart < min)
      return `Please add at least ${min - countInCart}`;
    if (hasMaximum && countInCart > 0 && !hasReachedMax)
      return `Add up to ${max - countInCart} more`;
    if (hasReachedMax && max > 1) return "You cannot add any more";
    if (countInCart === 0 && max > 1 && canSelect) return `Add up to ${max}`; // Check canSelect here
    return "";
  }

  const subtitle = getSubtitle();

  return (
    <div
      className={classNames(
        "flex items-center justify-between rounded-md border border-primary/30 relative transition opacity-100 pr-2"
      )}
    >
      <button
        type="button"
        className={classNames("flex justify-between py-3 px-2 text-sm w-full")}
        onClick={() => {
          setTapped(false);
          if (!isSelected && canSelectNow) {
            // Select if not selected and allowed
            selectItem(id);
          } else if (isSelected && !canSelectMultiple) {
            // If single select, toggle off if allowed
            if (canUnselect) {
              unselectItem(id);
            }
          } else if (!isSelected && !canSelectNow && canSelectMultiple) {
            // Special case: Can select multiple, but group max is reached. Do nothing on main click.
          } else if (isSelected && canSelectMultiple) {
            // If multiple selection & selected, primary click does nothing (handled by +/-)
          } else {
            // Default case if not selected and cannot select now (single select group already full?)
            // Potentially do nothing or handle edge case
          }
          setTapped(true); // Trigger tap animation regardless?
        }}
      >
        <div className="flex gap-2 items-center max-w-[85%]">
          <div
            className={classNames(
              "rounded-full border border-primary/30 p-2 h-5 w-5 flex items-center justify-center",
              isSelected
                ? "bg-primary text-primary-foreground"
                : "bg-transparent text-foreground",
              tapped && "animate-button-tap"
            )}
            onAnimationEnd={() => setTapped(false)}
          >
            {isSelected && (
              <div className="shrink-0 text-[10px]">
                {max === 1 ? (
                  <CheckIcon
                    strokeWidth="2px"
                    className={classNames("h-3 w-3")}
                  />
                ) : (
                  countInCart.toString()
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col justify-start items-start gap-y-0">
            <p className="font-medium tracking-tight whitespace-nowrap text-ellipsis overflow-hidden heading-typography">
              {name}
            </p>
            <span className="text-[10px] leading-[10px] text-muted-foreground">
              {subtitle}
            </span>
          </div>
        </div>
      </button>

      <div className={cn("flex gap-x-2 items-center pl-2")}>
        <span className="text-xs whitespace-nowrap">
          {price > 0 ? `+ ${formatPriceToDkk(price)}` : ""}
        </span>

        {isSelected && canSelectMultiple && (
          <motion.div
            transition={{ type: "ease-in-out" }}
            key="buttons"
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-center gap-x-1"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setTapped(false);
                unselectItem(id);
                setTapped(true);
              }}
              className="transition bg-background text-foreground flex items-center justify-center rounded-full border p-1 active:bg-primary active:text-primary-foreground"
              disabled={!canUnselect}
            >
              <MinusIcon className="w-3 h-3" strokeWidth="2px" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setTapped(false);
                selectItem(id);
                setTapped(true);
              }}
              className="transition bg-background text-foreground flex items-center justify-center disabled:opacity-75 rounded-full border p-1 active:bg-primary active:text-primary-foreground"
              disabled={!canSelectNow}
            >
              <PlusIcon className="w-3 h-3" strokeWidth="2px" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
